import { useState } from 'react'
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Chip,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TextField,
    InputAdornment,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Switch,
    FormControlLabel,
    Alert,
    TablePagination,
    Tabs,
    Tab,
    Divider,
} from '@mui/material'
import {
    Search,
    People,
    Business,
    Assessment,
    Storage,
    TrendingUp,
    Edit,
    Delete,
    Visibility,
    PersonAdd,
    LockReset,
    AdminPanelSettings,
} from '@mui/icons-material'

import {
    useGetAdminStatsQuery,
    useGetAdminTenantsQuery,
    useGetAdminUsersQuery,
    useGetAdminSubscriptionsQuery,
    useUpdateAdminTenantMutation,
    useDeleteAdminTenantMutation,
    useUpdateAdminUserMutation,
    useDeleteAdminUserMutation,
    useResetUserPasswordMutation,
    useUpdateAdminSubscriptionMutation,
    useCreateAdminSubscriptionMutation,
    useGetAdminTenantQuery,
    useGetAuditLogsQuery,
    type AdminTenantRow,
    type AdminUserRow,
    type AdminSubscriptionRow,
    type OrgOption,
    type UserOption,
} from '@/apis/adminApi'
import { OrgPicker, UserPicker } from '@/components/admin/EntityPickers'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { EmptyState } from '@/components/shared/EmptyState'

// ── Stat Card ────────────────────────────────────────────────

function StatCard({
    title,
    value,
    icon,
    color = 'primary',
}: {
    title: string
    value: string | number
    icon: React.ReactNode
    color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'
}) {
    return (
        <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                    sx={{
                        bgcolor: `${color}.main`,
                        color: 'white',
                        borderRadius: 2,
                        p: 1.5,
                        display: 'flex',
                    }}
                >
                    {icon}
                </Box>
                <Box>
                    <Typography variant="body2" color="text.secondary">
                        {title}
                    </Typography>
                    <Typography variant="h5" fontWeight={700}>
                        {typeof value === 'number' ? value.toLocaleString() : value}
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    )
}

// ── Admin Dashboard Tab ──────────────────────────────────────

function AdminStatsTab() {
    const { data: stats, isLoading } = useGetAdminStatsQuery()

    if (isLoading || !stats) {
        return (
            <Typography color="text.secondary" sx={{ p: 3 }}>
                Loading platform stats…
            </Typography>
        )
    }

    return (
        <Box>
            <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <StatCard title="Total Tenants" value={stats.tenants} icon={<Business />} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <StatCard
                        title="Total Users"
                        value={stats.users}
                        icon={<People />}
                        color="secondary"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <StatCard
                        title="Total Projects"
                        value={stats.projects}
                        icon={<Assessment />}
                        color="info"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <StatCard
                        title="Monthly Events"
                        value={stats.totalMonthlyEvents}
                        icon={<TrendingUp />}
                        color="success"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <StatCard
                        title="Active Subscriptions"
                        value={stats.activeSubscriptions}
                        icon={<Storage />}
                        color="warning"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <StatCard
                        title="Signups (30d)"
                        value={stats.recentSignups30d}
                        icon={<PersonAdd />}
                        color="primary"
                    />
                </Grid>
            </Grid>

            <Typography variant="h6" sx={{ mb: 2 }}>
                Plan Breakdown
            </Typography>
            <Card>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Plan</TableCell>
                            <TableCell align="right">Tenants</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {stats.planBreakdown.map(row => (
                            <TableRow key={row.plan}>
                                <TableCell>
                                    <Chip
                                        label={row.plan}
                                        size="small"
                                        sx={{ textTransform: 'capitalize' }}
                                    />
                                </TableCell>
                                <TableCell align="right">{row.count}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Card>
        </Box>
    )
}

// ── Tenants Tab ──────────────────────────────────────────────

function AdminTenantsTab() {
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(0)
    const [editTenant, setEditTenant] = useState<AdminTenantRow | null>(null)
    const [editPlan, setEditPlan] = useState('')
    const [editStatus, setEditStatus] = useState('')
    const [editAiAddon, setEditAiAddon] = useState(false)
    const [editReason, setEditReason] = useState('')
    const [editTicketRef, setEditTicketRef] = useState('')
    const [deleteTenantId, setDeleteTenantId] = useState<string | null>(null)
    const [detailTenantId, setDetailTenantId] = useState<string | null>(null)

    const { data, isLoading } = useGetAdminTenantsQuery({
        page: page + 1,
        limit: 20,
        search: search || undefined,
    })
    const [updateTenant] = useUpdateAdminTenantMutation()
    const [deleteTenant] = useDeleteAdminTenantMutation()
    const { data: tenantDetail } = useGetAdminTenantQuery(detailTenantId!, {
        skip: !detailTenantId,
    })

    const handleOpenEdit = (tenant: AdminTenantRow) => {
        setEditTenant(tenant)
        setEditPlan(tenant.plan)
        setEditStatus(tenant.status)
        setEditAiAddon(false)
        setEditReason('')
        setEditTicketRef('')
    }

    const handleSaveEdit = async () => {
        if (!editTenant) {
            return
        }
        await updateTenant({
            id: editTenant.id,
            plan: editPlan !== editTenant.plan ? editPlan : undefined,
            status: editStatus !== editTenant.status ? editStatus : undefined,
            aiAddon: editAiAddon,
            reason: editReason.trim() || undefined,
            ticketRef: editTicketRef.trim() || undefined,
        })
        setEditTenant(null)
    }

    const handleDelete = async () => {
        if (!deleteTenantId) {
            return
        }
        await deleteTenant({ id: deleteTenantId })
        setDeleteTenantId(null)
    }

    const statusColor = (s: string) => {
        if (s === 'active') {
            return 'success' as const
        }
        if (s === 'suspended') {
            return 'error' as const
        }
        return 'default' as const
    }

    return (
        <Box>
            <TextField
                size="small"
                placeholder="Search tenants…"
                value={search}
                onChange={e => {
                    setSearch(e.target.value)
                    setPage(0)
                }}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        ),
                    },
                }}
                sx={{ mb: 2, width: 320 }}
            />

            {isLoading ? (
                <Typography color="text.secondary">Loading…</Typography>
            ) : !data?.items.length ? (
                <EmptyState
                    icon={<Business />}
                    title="No tenants found"
                    description="No tenants match your search."
                />
            ) : (
                <Card>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Slug</TableCell>
                                <TableCell>Plan</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Members</TableCell>
                                <TableCell align="right">Projects</TableCell>
                                <TableCell>Created</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.items.map(t => (
                                <TableRow key={t.id}>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={600}>
                                            {t.name}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            fontFamily="monospace"
                                        >
                                            {t.slug}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={t.plan}
                                            size="small"
                                            sx={{ textTransform: 'capitalize' }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={t.status}
                                            size="small"
                                            color={statusColor(t.status)}
                                            sx={{ textTransform: 'capitalize' }}
                                        />
                                    </TableCell>
                                    <TableCell align="right">{t.memberCount}</TableCell>
                                    <TableCell align="right">{t.projectCount}</TableCell>
                                    <TableCell>
                                        {new Date(t.createdAt).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="View details">
                                            <IconButton
                                                size="small"
                                                onClick={() => setDetailTenantId(t.id)}
                                            >
                                                <Visibility fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Edit">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenEdit(t)}
                                            >
                                                <Edit fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => setDeleteTenantId(t.id)}
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {data.pagination.totalPages > 1 && (
                        <TablePagination
                            component="div"
                            count={data.pagination.total}
                            page={page}
                            onPageChange={(_e, p) => setPage(p)}
                            rowsPerPage={20}
                            rowsPerPageOptions={[20]}
                        />
                    )}
                </Card>
            )}

            {/* Edit Dialog */}
            <Dialog open={!!editTenant} onClose={() => setEditTenant(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Edit Tenant: {editTenant?.name}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Plan</InputLabel>
                        <Select
                            value={editPlan}
                            label="Plan"
                            onChange={e => setEditPlan(e.target.value)}
                        >
                            {['free', 'starter', 'growth', 'business', 'enterprise'].map(p => (
                                <MenuItem key={p} value={p} sx={{ textTransform: 'capitalize' }}>
                                    {p}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={editStatus}
                            label="Status"
                            onChange={e => setEditStatus(e.target.value)}
                        >
                            {['active', 'suspended', 'archived'].map(s => (
                                <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>
                                    {s}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControlLabel
                        control={
                            <Switch checked={editAiAddon} onChange={(_e, v) => setEditAiAddon(v)} />
                        }
                        label="AI Add-on Enabled"
                    />
                    <TextField
                        size="small"
                        label="Reason (audit log)"
                        value={editReason}
                        onChange={e => setEditReason(e.target.value)}
                        multiline
                        minRows={2}
                        slotProps={{ htmlInput: { maxLength: 500 } }}
                        helperText="Recorded with your name in the org's audit log; the customer sees only that YOM staff made the change."
                    />
                    <TextField
                        size="small"
                        label="Ticket ref"
                        placeholder="GRV-20261102-01"
                        value={editTicketRef}
                        onChange={e => setEditTicketRef(e.target.value)}
                        slotProps={{ htmlInput: { maxLength: 64 } }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditTenant(null)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveEdit}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Detail Dialog */}
            <Dialog
                open={!!detailTenantId}
                onClose={() => setDetailTenantId(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Tenant Details</DialogTitle>
                <DialogContent>
                    {tenantDetail ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Typography variant="h6">{tenantDetail.name}</Typography>
                            <Divider />
                            <Typography variant="body2">
                                <strong>ID:</strong> {tenantDetail.id}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Slug:</strong> {tenantDetail.slug}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Plan:</strong> {tenantDetail.plan}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Status:</strong> {tenantDetail.status}
                            </Typography>
                            <Typography variant="body2">
                                <strong>AI Add-on:</strong> {tenantDetail.aiAddon ? 'Yes' : 'No'}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Region:</strong> {tenantDetail.region}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Created:</strong>{' '}
                                {new Date(tenantDetail.createdAt).toLocaleString()}
                            </Typography>

                            <Divider />
                            <Typography variant="subtitle2">
                                Members ({tenantDetail.members.length})
                            </Typography>
                            {tenantDetail.members.map(m => (
                                <Typography key={m.id} variant="body2">
                                    {m.name} ({m.email}) — {m.role}
                                </Typography>
                            ))}

                            <Divider />
                            <Typography variant="subtitle2">
                                Projects ({tenantDetail.projects.length})
                            </Typography>
                            {tenantDetail.projects.map(p => (
                                <Typography key={p.id} variant="body2">
                                    {p.name} — {p.domain}
                                </Typography>
                            ))}

                            {tenantDetail.subscription && (
                                <>
                                    <Divider />
                                    <Typography variant="subtitle2">Subscription</Typography>
                                    <Typography variant="body2">
                                        {tenantDetail.subscription.plan} (
                                        {tenantDetail.subscription.status}) via{' '}
                                        {tenantDetail.subscription.provider}
                                    </Typography>
                                </>
                            )}
                        </Box>
                    ) : (
                        <Typography color="text.secondary">Loading…</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailTenantId(null)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirm */}
            <ConfirmDialog
                open={!!deleteTenantId}
                title="Delete Tenant?"
                message="This will soft-delete the tenant. Their data will be marked as deleted."
                onConfirm={handleDelete}
                onCancel={() => setDeleteTenantId(null)}
            />
        </Box>
    )
}

// ── Users Tab ────────────────────────────────────────────────

function AdminUsersTab() {
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(0)
    const [editUser, setEditUser] = useState<AdminUserRow | null>(null)
    const [editSuperAdmin, setEditSuperAdmin] = useState(false)
    const [editForceChange, setEditForceChange] = useState(false)
    const [deleteUserId, setDeleteUserId] = useState<string | null>(null)
    const [resetDialog, setResetDialog] = useState<string | null>(null)
    const [newPassword, setNewPassword] = useState('')
    const [successMsg, setSuccessMsg] = useState('')

    const { data, isLoading } = useGetAdminUsersQuery({
        page: page + 1,
        limit: 20,
        search: search || undefined,
    })
    const [updateUser] = useUpdateAdminUserMutation()
    const [deleteUser] = useDeleteAdminUserMutation()
    const [resetPassword] = useResetUserPasswordMutation()

    const handleOpenEdit = (user: AdminUserRow) => {
        setEditUser(user)
        setEditSuperAdmin(!!user.isSuperadmin)
        setEditForceChange(false)
    }

    const handleSaveEdit = async () => {
        if (!editUser) {
            return
        }
        await updateUser({
            id: editUser.id,
            isSuperAdmin: editSuperAdmin,
            forcePasswordChange: editForceChange || undefined,
        })
        setEditUser(null)
        setSuccessMsg('User updated successfully')
    }

    const handleDelete = async () => {
        if (!deleteUserId) {
            return
        }
        await deleteUser(deleteUserId)
        setDeleteUserId(null)
        setSuccessMsg('User deleted')
    }

    const handleResetPassword = async () => {
        if (!resetDialog || !newPassword) {
            return
        }
        await resetPassword({ userId: resetDialog, newPassword, forceChange: true })
        setResetDialog(null)
        setNewPassword('')
        setSuccessMsg('Password reset successfully')
    }

    return (
        <Box>
            {successMsg && (
                <Alert severity="success" onClose={() => setSuccessMsg('')} sx={{ mb: 2 }}>
                    {successMsg}
                </Alert>
            )}

            <TextField
                size="small"
                placeholder="Search users…"
                value={search}
                onChange={e => {
                    setSearch(e.target.value)
                    setPage(0)
                }}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        ),
                    },
                }}
                sx={{ mb: 2, width: 320 }}
            />

            {isLoading ? (
                <Typography color="text.secondary">Loading…</Typography>
            ) : !data?.items.length ? (
                <EmptyState
                    icon={<People />}
                    title="No users found"
                    description="No users match your search."
                />
            ) : (
                <Card>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Verified</TableCell>
                                <TableCell>Superadmin</TableCell>
                                <TableCell>Tenants</TableCell>
                                <TableCell>Last Login</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.items.map(u => (
                                <TableRow key={u.id}>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={600}>
                                            {u.name}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{u.email}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={u.emailVerified ? 'Yes' : 'No'}
                                            size="small"
                                            color={u.emailVerified ? 'success' : 'default'}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {u.isSuperadmin ? (
                                            <Chip label="Superadmin" size="small" color="primary" />
                                        ) : (
                                            '—'
                                        )}
                                    </TableCell>
                                    <TableCell>{u.tenantCount}</TableCell>
                                    <TableCell>
                                        {u.lastLoginAt
                                            ? new Date(u.lastLoginAt).toLocaleDateString()
                                            : 'Never'}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Edit">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenEdit(u)}
                                            >
                                                <Edit fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Reset Password">
                                            <IconButton
                                                size="small"
                                                onClick={() => setResetDialog(u.id)}
                                            >
                                                <LockReset fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => setDeleteUserId(u.id)}
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {data.pagination.totalPages > 1 && (
                        <TablePagination
                            component="div"
                            count={data.pagination.total}
                            page={page}
                            onPageChange={(_e, p) => setPage(p)}
                            rowsPerPage={20}
                            rowsPerPageOptions={[20]}
                        />
                    )}
                </Card>
            )}

            {/* Edit User Dialog */}
            <Dialog open={!!editUser} onClose={() => setEditUser(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Edit User: {editUser?.name}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        {editUser?.email}
                    </Typography>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={editSuperAdmin}
                                onChange={(_e, v) => setEditSuperAdmin(v)}
                            />
                        }
                        label="Superadmin"
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={editForceChange}
                                onChange={(_e, v) => setEditForceChange(v)}
                            />
                        }
                        label="Force Password Change on Next Login"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditUser(null)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveEdit}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reset Password Dialog */}
            <Dialog
                open={!!resetDialog}
                onClose={() => setResetDialog(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Reset Password</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <TextField
                        label="New Password"
                        type="password"
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        fullWidth
                        inputProps={{ minLength: 8 }}
                        helperText="Minimum 8 characters. User will be forced to change on next login."
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResetDialog(null)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleResetPassword}
                        disabled={newPassword.length < 8}
                    >
                        Reset
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirm */}
            <ConfirmDialog
                open={!!deleteUserId}
                title="Delete User?"
                message="This will permanently delete the user and remove all their tenant memberships."
                onConfirm={handleDelete}
                onCancel={() => setDeleteUserId(null)}
            />
        </Box>
    )
}

// ── Subscriptions Tab ────────────────────────────────────────

function AdminSubscriptionsTab() {
    const [page, setPage] = useState(0)
    const [statusFilter, setStatusFilter] = useState('')
    const [createOpen, setCreateOpen] = useState(false)
    const [createOrg, setCreateOrg] = useState<OrgOption | null>(null)
    const [createPlan, setCreatePlan] = useState<'free' | 'payg' | 'complimentary'>('complimentary')
    const [createLimit, setCreateLimit] = useState('1000000')
    const [createNote, setCreateNote] = useState('')
    const [createPeriodEnd, setCreatePeriodEnd] = useState('')
    const [createError, setCreateError] = useState('')
    const [successMsg, setSuccessMsg] = useState('')

    const { data, isLoading } = useGetAdminSubscriptionsQuery({
        page: page + 1,
        limit: 20,
        status: statusFilter || undefined,
    })
    const [createSubscription] = useCreateAdminSubscriptionMutation()
    const [updateSubscription] = useUpdateAdminSubscriptionMutation()

    const limitValue = Number(createLimit)
    const limitValid =
        createPlan !== 'complimentary' || (Number.isInteger(limitValue) && limitValue >= 1000)
    const canCreate = !!createOrg && !!createPeriodEnd && limitValid

    const handleCreate = async () => {
        if (!createOrg || !canCreate) {
            return
        }
        setCreateError('')
        try {
            await createSubscription({
                tenantId: createOrg.id,
                plan: createPlan,
                periodEnd: new Date(`${createPeriodEnd}T23:59:59.000Z`).toISOString(),
                ...(createPlan === 'complimentary' ? { monthlyEventLimit: limitValue } : {}),
                ...(createNote.trim() ? { note: createNote.trim() } : {}),
            }).unwrap()
        } catch {
            setCreateError('Could not create the subscription — check the inputs and try again.')
            return
        }
        setCreateOpen(false)
        setCreateOrg(null)
        setCreateNote('')
        setSuccessMsg(`Subscription created for ${createOrg.name}`)
    }

    const handleToggleStatus = async (sub: AdminSubscriptionRow) => {
        const newStatus = sub.status === 'active' ? 'cancelled' : 'active'
        await updateSubscription({ id: sub.id, status: newStatus })
        setSuccessMsg(`Subscription ${newStatus}`)
    }

    return (
        <Box>
            {successMsg && (
                <Alert severity="success" onClose={() => setSuccessMsg('')} sx={{ mb: 2 }}>
                    {successMsg}
                </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                        value={statusFilter}
                        label="Status"
                        onChange={e => {
                            setStatusFilter(e.target.value)
                            setPage(0)
                        }}
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="past_due">Past Due</MenuItem>
                        <MenuItem value="cancelled">Cancelled</MenuItem>
                        <MenuItem value="trialing">Trialing</MenuItem>
                    </Select>
                </FormControl>
                <Button variant="contained" size="small" onClick={() => setCreateOpen(true)}>
                    Create Manual Subscription
                </Button>
            </Box>

            {isLoading ? (
                <Typography color="text.secondary">Loading…</Typography>
            ) : !data?.items.length ? (
                <EmptyState
                    icon={<Storage />}
                    title="No subscriptions"
                    description="No subscriptions match your filters."
                />
            ) : (
                <Card>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Tenant</TableCell>
                                <TableCell>Plan</TableCell>
                                <TableCell>Provider</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Period End</TableCell>
                                <TableCell>Created</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.items.map(s => (
                                <TableRow key={s.id}>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={600}>
                                            {s.tenantName}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {s.tenantSlug}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={s.plan}
                                            size="small"
                                            sx={{ textTransform: 'capitalize' }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ textTransform: 'capitalize' }}>
                                        {s.provider}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={s.status}
                                            size="small"
                                            color={
                                                s.status === 'active'
                                                    ? 'success'
                                                    : s.status === 'past_due'
                                                      ? 'warning'
                                                      : 'default'
                                            }
                                            sx={{ textTransform: 'capitalize' }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {s.currentPeriodEnd
                                            ? new Date(s.currentPeriodEnd).toLocaleDateString()
                                            : '—'}
                                    </TableCell>
                                    <TableCell>
                                        {new Date(s.createdAt).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Button size="small" onClick={() => handleToggleStatus(s)}>
                                            {s.status === 'active' ? 'Cancel' : 'Activate'}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {data.pagination.totalPages > 1 && (
                        <TablePagination
                            component="div"
                            count={data.pagination.total}
                            page={page}
                            onPageChange={(_e, p) => setPage(p)}
                            rowsPerPage={20}
                            rowsPerPageOptions={[20]}
                        />
                    )}
                </Card>
            )}

            {/* Create Subscription Dialog */}
            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Create Manual Subscription</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                    {createError && <Alert severity="error">{createError}</Alert>}
                    <OrgPicker value={createOrg} onChange={setCreateOrg} required />
                    <FormControl size="small" fullWidth>
                        <InputLabel>Plan</InputLabel>
                        <Select
                            value={createPlan}
                            label="Plan"
                            onChange={e => setCreatePlan(e.target.value as typeof createPlan)}
                        >
                            <MenuItem value="complimentary">
                                Complimentary — all features, no charge, capped
                            </MenuItem>
                            <MenuItem value="payg">
                                Pay as you go — all features, usage billed
                            </MenuItem>
                            <MenuItem value="free">Free</MenuItem>
                        </Select>
                    </FormControl>
                    {createPlan === 'complimentary' && (
                        <TextField
                            label="Monthly event limit"
                            type="number"
                            value={createLimit}
                            onChange={e => setCreateLimit(e.target.value)}
                            fullWidth
                            size="small"
                            error={!limitValid}
                            helperText={
                                limitValid
                                    ? `${limitValue.toLocaleString()} events / month — ingest returns 429 above this until the next month`
                                    : 'At least 1,000'
                            }
                        />
                    )}
                    <TextField
                        label="Note (optional)"
                        value={createNote}
                        onChange={e => setCreateNote(e.target.value)}
                        fullWidth
                        size="small"
                        helperText="Recorded in the audit log"
                    />
                    <TextField
                        label="Period End Date"
                        type="date"
                        value={createPeriodEnd}
                        onChange={e => setCreatePeriodEnd(e.target.value)}
                        fullWidth
                        size="small"
                        slotProps={{ inputLabel: { shrink: true } }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreate} disabled={!canCreate}>
                        Create
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

// ── Audit Logs Tab ───────────────────────────────────────────

function AdminAuditLogsTab() {
    const [page, setPage] = useState(0)
    const [org, setOrg] = useState<OrgOption | null>(null)
    const [user, setUser] = useState<UserOption | null>(null)
    const { data, isLoading } = useGetAuditLogsQuery({
        page: page + 1,
        limit: 50,
        tenantId: org?.id,
        userId: user?.id,
    })

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 320px' }}>
                    <OrgPicker
                        label="Filter by organization"
                        value={org}
                        onChange={v => {
                            setOrg(v)
                            setPage(0)
                        }}
                    />
                </Box>
                <Box sx={{ flex: '1 1 320px' }}>
                    <UserPicker
                        label="Filter by user"
                        value={user}
                        onChange={v => {
                            setUser(v)
                            setPage(0)
                        }}
                    />
                </Box>
            </Box>
            {isLoading ? (
                <Typography color="text.secondary">Loading…</Typography>
            ) : !data?.items.length ? (
                <EmptyState
                    icon={<Assessment />}
                    title="No audit logs"
                    description="No audit logs recorded yet."
                />
            ) : (
                <Card>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Time</TableCell>
                                <TableCell>User</TableCell>
                                <TableCell>Tenant</TableCell>
                                <TableCell>Action</TableCell>
                                <TableCell>Resource</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.items.map(log => (
                                <TableRow key={log.id}>
                                    <TableCell>
                                        {new Date(log.createdAt).toLocaleString()}
                                    </TableCell>
                                    <TableCell>{log.userEmail ?? '—'}</TableCell>
                                    <TableCell>{log.tenantName ?? '—'}</TableCell>
                                    <TableCell>
                                        <Chip label={log.action} size="small" />
                                    </TableCell>
                                    <TableCell>
                                        {log.resourceType}
                                        {log.resourceId ? ` (${log.resourceId})` : ''}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {data.pagination.totalPages > 1 && (
                        <TablePagination
                            component="div"
                            count={data.pagination.total}
                            page={page}
                            onPageChange={(_e, p) => setPage(p)}
                            rowsPerPage={50}
                            rowsPerPageOptions={[50]}
                        />
                    )}
                </Card>
            )}
        </Box>
    )
}

// ── Main Admin Page ──────────────────────────────────────────

export function AdminPage() {
    const [tab, setTab] = useState(0)

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <AdminPanelSettings color="primary" fontSize="large" />
                <Box>
                    <Typography variant="h5">Platform Admin</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Superadmin panel — manage all tenants, users, and subscriptions
                    </Typography>
                </Box>
            </Box>

            <Tabs
                value={tab}
                onChange={(_e, v: number) => setTab(v)}
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
            >
                <Tab label="Overview" />
                <Tab label="Tenants" />
                <Tab label="Users" />
                <Tab label="Subscriptions" />
                <Tab label="Audit Logs" />
            </Tabs>

            {tab === 0 && <AdminStatsTab />}
            {tab === 1 && <AdminTenantsTab />}
            {tab === 2 && <AdminUsersTab />}
            {tab === 3 && <AdminSubscriptionsTab />}
            {tab === 4 && <AdminAuditLogsTab />}
        </Box>
    )
}
